/**
 * Terms of Use template — renderer + editor + default data.
 */

import { useState } from "react";
import { ChevronDown, ChevronUp, Plus, Trash2 } from "lucide-react";

export const TERMS_DEFAULT = {
  hero: {
    eyebrow: "POLICIES & GUIDELINES",
    headline: "Terms of Use",
    lead: "Everything you need to know about using the IMPORTREND website, placing orders, and shopping with confidence.",
    note: "Please read these terms before using our services.",
    art_icon: "📋",
    float1: "✓ Clear guidelines",
    float2: "🔒 Secure shopping",
  },
  intro: {
    headline: "Welcome to IMPORTREND",
    body: "These Terms of Use govern access to and use of the IMPORTREND website and related services. By using the website or placing an order, you agree to these terms, subject to applicable law.",
  },
  sections: [
    { icon: "🌐", title: "1. Website Use", body: "Use this website only for lawful purposes. Information displayed on the site is intended to help you browse products, understand our services, and make purchases. We may update website content and features from time to time." },
    { icon: "👤", title: "2. Customer Accounts", body: "You are responsible for providing accurate account and contact information and for keeping your login credentials confidential. Please notify us if you suspect unauthorized access to your account." },
    { icon: "🏷️", title: "3. Product Information & Pricing", body: "We aim to keep product descriptions, images, availability, and prices accurate. Minor variations in appearance may occur due to screen settings or manufacturing differences. Prices and availability may change before an order is confirmed." },
    { icon: "🛒", title: "4. Orders & Payments", body: "Please review your cart, delivery address, and payment details carefully before confirming your order. Orders are subject to successful payment (where applicable), verification, and product availability. If an order cannot be fulfilled, we will contact you regarding the appropriate resolution." },
    { icon: "🚚", title: "5. Shipping & Delivery", body: "Delivery estimates, serviceability, and applicable shipping charges are provided during checkout or in order communications. Delivery times may vary due to location, logistics constraints, weather, holidays, or other circumstances beyond our reasonable control. Please ensure your shipping information is correct." },
    { icon: "📦", title: "6. Returns & Refunds", body: "Return or refund requests are accepted only for products received damaged or different from what was ordered, subject to applicable consumer rights. Report an eligible issue within 24 hours of receiving the product. Include your order details, a description of the issue, and clear supporting photos when contacting support. Requests are reviewed before a return or refund is approved." },
    { icon: "🚫", title: "7. Cancellation Policy", body: "Once an order has been confirmed, cancellation is not permitted and no refund is offered for cancellation requests, except where a refund or other remedy is required by applicable law. Please verify your order information before confirming your purchase." },
    { icon: "🛡️", title: "8. Acceptable Use", body: "Do not misuse the website, attempt unauthorized access, submit fraudulent orders, interfere with its operation, or upload harmful code. We may restrict access where reasonably necessary to protect customers and the website." },
    { icon: "©️", title: "9. Intellectual Property", body: "Website content, graphics, branding, and original materials belonging to IMPORTREND are protected by applicable intellectual property laws. You may not reproduce or commercially use them without permission, except as permitted by law." },
    { icon: "⚖️", title: "10. Responsibility & Liability", body: "We work to provide accurate information and dependable service, but uninterrupted website availability cannot be guaranteed. Any limitation of liability applies only to the extent permitted by applicable law and does not affect mandatory consumer protections." },
    { icon: "🔄", title: "11. Changes to These Terms", body: "We may revise these terms to reflect operational, legal, or service changes. The updated version will be posted on this page. Material changes will be communicated where required by law." },
    { icon: "💬", title: "12. Questions & Support", body: "For questions about these terms or an existing order, contact our support team during business hours.\n\nEmail: support@importrend.com\nPhone: +91 7011608563\nHours: Monday–Saturday, 09:00–18:00 (IST)" },
  ],
  cta: {
    headline: "Need Help Understanding Our Terms?",
    body: "Our support team is here to answer your questions about orders, policies, and shopping with IMPORTREND.",
    btn1_text: "✉ Email Support",
    btn1_href: "mailto:support@importrend.com",
    btn2_text: "☎ Call Support",
    btn2_href: "tel:+917011608563",
  },
};

const TU_CSS = `
.tu-root{--ink:#3c2e25;--orange:#ed792f;--dark:#513421;--cream:#fff8f0;--peach:#ffe8d4;--muted:#77695d;--line:#f1ddc9;color:var(--ink);background:#fffdf9;font-family:system-ui,-apple-system,"Segoe UI",Arial,sans-serif;line-height:1.65}
.tu-root *{box-sizing:border-box}
.tu-root a{color:#a64b13}
.tu-root h1,.tu-root h2,.tu-root h3,.tu-root p,.tu-root ul{margin-top:0}
.tu-wrap{max-width:1160px;margin:auto;padding:0 24px}
.tu-hero{background:linear-gradient(115deg,#fff4e7,#ffe6d0);overflow:hidden}
.tu-hero-inner{display:grid;grid-template-columns:1.25fr .75fr;align-items:center;gap:36px;padding:74px 24px}
.tu-eyebrow{display:inline-block;background:#fff;padding:5px 15px;border-radius:99px;color:#a95017;font-size:13px;font-weight:800;letter-spacing:.07em}
.tu-hero h1{font-size:clamp(38px,5.2vw,66px);line-height:1.1;margin:19px 0 14px;letter-spacing:-.03em}
.tu-hero .tu-lead{font-size:18px;max-width:620px;color:#6b5444;margin-bottom:14px}
.tu-hero-art{min-height:260px;display:grid;place-items:center;position:relative}
.tu-illustration{font-size:120px;filter:drop-shadow(0 20px 20px #c7793850)}
.tu-float{position:absolute;background:white;padding:10px 17px;border-radius:18px;box-shadow:0 12px 25px #9d622c20;font-weight:800;font-size:.9rem}
.tu-f1{top:15px;right:8px}.tu-f2{bottom:14px;left:4px}
.tu-intro{padding:55px 24px 12px;text-align:center;max-width:1160px;margin:auto}
.tu-intro h2{font-size:clamp(1.7rem,2.5vw,2.4rem);margin-bottom:12px}
.tu-intro p{max-width:760px;margin:auto;color:var(--muted)}
.tu-layout{display:grid;grid-template-columns:250px minmax(0,1fr);gap:28px;padding:30px 24px 70px;max-width:1160px;margin:auto}
.tu-nav{position:sticky;top:22px;align-self:start;border:1px solid var(--line);border-radius:22px;background:white;padding:20px}
.tu-nav strong{display:block;margin-bottom:12px;font-size:.9rem}
.tu-nav a{display:block;text-decoration:none;color:#735744;padding:7px 10px;border-radius:9px;font-size:14px}
.tu-nav a:hover{background:var(--cream);color:#bd581b}
.tu-sections{display:grid;gap:20px}
.tu-card{background:white;border:1px solid var(--line);border-radius:22px;padding:29px;box-shadow:0 7px 25px #6c391408;scroll-margin-top:22px}
.tu-card:nth-child(even){background:#fff9f3}
.tu-card-title{display:flex;align-items:center;gap:14px;margin-bottom:12px}
.tu-icon{width:48px;height:48px;display:grid;place-items:center;border-radius:15px;background:#ffead8;font-size:25px;flex:none}
.tu-card p,.tu-card li{color:#66584c;margin-bottom:.6rem}
.tu-card ul{padding-left:21px;margin:10px 0 0}
.tu-notice{padding:18px 20px;border-left:4px solid var(--orange);border-radius:12px;background:#fff0df;margin-top:15px;color:#6b4831}
.tu-support{background:linear-gradient(120deg,#f9a05e,#e87934);border-radius:28px;padding:45px;color:white;text-align:center;margin:0 auto 65px;max-width:1160px}
.tu-support h2{color:white;font-size:clamp(1.5rem,2.5vw,2rem);margin-bottom:10px}
.tu-support p{max-width:650px;margin:0 auto 20px;color:#fff5e8}
.tu-btns{display:flex;justify-content:center;flex-wrap:wrap;gap:12px}
.tu-btn{background:white;color:#8e451e;text-decoration:none;padding:12px 20px;border-radius:12px;font-weight:800;font-size:.9rem}
.tu-btn-sec{background:#a34c23;color:white}
@media(max-width:800px){.tu-hero-inner{grid-template-columns:1fr;padding:50px 24px 35px}.tu-hero-art{min-height:170px}.tu-illustration{font-size:85px}.tu-layout{grid-template-columns:1fr}.tu-nav{position:static;display:flex;overflow:auto;gap:4px;padding:10px}.tu-nav strong{display:none}.tu-nav a{white-space:nowrap}.tu-card{padding:23px}.tu-intro{padding-top:42px}.tu-support{margin:0 18px 50px;border-radius:22px;padding:35px 20px}}
`;

export function TermsOfUseRenderer({ data }) {
  const d = data || TERMS_DEFAULT;
  const h = d.hero     || TERMS_DEFAULT.hero;
  const intro = d.intro || TERMS_DEFAULT.intro;
  const secs = d.sections || TERMS_DEFAULT.sections;
  const cta = d.cta    || TERMS_DEFAULT.cta;

  return (
    <div className="tu-root">
      <style>{TU_CSS}</style>

      <section className="tu-hero">
        <div className="tu-wrap tu-hero-inner">
          <div>
            <span className="tu-eyebrow">{h.eyebrow}</span>
            <h1>{h.headline}</h1>
            <p className="tu-lead">{h.lead}</p>
            {h.note && <p style={{fontWeight:700}}>{h.note}</p>}
          </div>
          <div className="tu-hero-art">
            <span className="tu-illustration">{h.art_icon}</span>
            <span className="tu-float tu-f1">{h.float1}</span>
            <span className="tu-float tu-f2">{h.float2}</span>
          </div>
        </div>
      </section>

      <div className="tu-intro">
        <h2>{intro.headline}</h2>
        <p>{intro.body}</p>
      </div>

      <div className="tu-layout">
        <nav className="tu-nav">
          <strong>On this page</strong>
          {secs.map((s, i) => (
            <a key={i} href={`#tu-sec-${i}`}>{s.title}</a>
          ))}
        </nav>
        <div className="tu-sections">
          {secs.map((s, i) => (
            <section key={i} id={`tu-sec-${i}`} className="tu-card">
              <div className="tu-card-title">
                <span className="tu-icon">{s.icon}</span>
                <h3 style={{margin:0,fontSize:"1.1rem"}}>{s.title}</h3>
              </div>
              {s.body.split("\n\n").map((para, j) => (
                <p key={j}>{para}</p>
              ))}
            </section>
          ))}
        </div>
      </div>

      <div className="tu-wrap">
        <div className="tu-support">
          <h2>{cta.headline}</h2>
          <p>{cta.body}</p>
          <div className="tu-btns">
            <a className="tu-btn" href={cta.btn1_href}>{cta.btn1_text}</a>
            <a className="tu-btn tu-btn-sec" href={cta.btn2_href}>{cta.btn2_text}</a>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, multiline = false, hint, rows = 3 }) {
  const cls = "w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:border-orange-400 bg-white resize-none";
  return (
    <div>
      <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1">{label}</label>
      {multiline
        ? <textarea rows={rows} value={value || ""} onChange={e => onChange(e.target.value)} className={cls} />
        : <input type="text" value={value || ""} onChange={e => onChange(e.target.value)} className={cls} />}
      {hint && <p className="text-[10px] text-slate-400 mt-0.5">{hint}</p>}
    </div>
  );
}

function SectionBlock({ title, children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-slate-200 rounded-xl overflow-hidden">
      <button type="button" onClick={() => setOpen(v => !v)}
        className="w-full flex items-center justify-between px-4 py-3 bg-slate-50 hover:bg-slate-100 transition-colors text-left">
        <span className="text-xs font-bold text-slate-700">{title}</span>
        {open ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
      </button>
      {open && <div className="px-4 py-4 space-y-3 bg-white">{children}</div>}
    </div>
  );
}

export function TermsOfUseEditor({ data, onChange }) {
  const d = data || TERMS_DEFAULT;
  const set = (section, field, value) => onChange({ ...d, [section]: { ...(d[section] || {}), [field]: value } });
  const setSec = (idx, field, value) => {
    const secs = [...(d.sections || [])];
    secs[idx] = { ...secs[idx], [field]: value };
    onChange({ ...d, sections: secs });
  };
  const addSec = () => onChange({ ...d, sections: [...(d.sections || []), { icon: "📌", title: "New Section", body: "Section content here." }] });
  const removeSec = (idx) => onChange({ ...d, sections: (d.sections || []).filter((_, i) => i !== idx) });

  const h = d.hero || {};
  const intro = d.intro || {};
  const secs = d.sections || [];
  const cta = d.cta || {};

  return (
    <div className="space-y-3">
      <div className="bg-orange-50 border border-orange-200 rounded-xl px-4 py-3 text-xs text-orange-700">
        <strong>Terms of Use Template</strong> — Edit the hero, intro, individual term sections, and the CTA strip.
      </div>

      <SectionBlock title="1. Hero" defaultOpen>
        <Field label="Eyebrow" value={h.eyebrow} onChange={v => set("hero","eyebrow",v)} />
        <Field label="Headline" value={h.headline} onChange={v => set("hero","headline",v)} />
        <Field label="Lead paragraph" value={h.lead} onChange={v => set("hero","lead",v)} multiline />
        <Field label="Note (bold line)" value={h.note} onChange={v => set("hero","note",v)} />
        <div className="grid grid-cols-3 gap-2">
          <Field label="Art icon (emoji)" value={h.art_icon} onChange={v => set("hero","art_icon",v)} />
          <Field label="Float tag 1" value={h.float1} onChange={v => set("hero","float1",v)} />
          <Field label="Float tag 2" value={h.float2} onChange={v => set("hero","float2",v)} />
        </div>
      </SectionBlock>

      <SectionBlock title="2. Introduction">
        <Field label="Headline" value={intro.headline} onChange={v => set("intro","headline",v)} />
        <Field label="Body" value={intro.body} onChange={v => set("intro","body",v)} multiline />
      </SectionBlock>

      <SectionBlock title="3. Term Sections">
        {secs.map((s, i) => (
          <div key={i} className="border border-slate-200 rounded-xl p-3 space-y-2 relative">
            <button type="button" onClick={() => removeSec(i)}
              className="absolute top-2 right-2 text-slate-300 hover:text-red-500"><Trash2 className="w-3.5 h-3.5" /></button>
            <div className="grid grid-cols-4 gap-2">
              <Field label="Icon" value={s.icon} onChange={v => setSec(i,"icon",v)} />
              <div className="col-span-3"><Field label="Title" value={s.title} onChange={v => setSec(i,"title",v)} /></div>
            </div>
            <Field label="Body text (use blank lines for paragraphs)" value={s.body} onChange={v => setSec(i,"body",v)} multiline rows={4} />
          </div>
        ))}
        <button type="button" onClick={addSec}
          className="flex items-center gap-1.5 text-xs text-orange-600 hover:text-orange-700 font-semibold">
          <Plus className="w-3.5 h-3.5" /> Add section
        </button>
      </SectionBlock>

      <SectionBlock title="4. CTA Strip">
        <Field label="Headline" value={cta.headline} onChange={v => set("cta","headline",v)} />
        <Field label="Body" value={cta.body} onChange={v => set("cta","body",v)} multiline />
        <div className="grid grid-cols-2 gap-2">
          <Field label="Button 1 text" value={cta.btn1_text} onChange={v => set("cta","btn1_text",v)} />
          <Field label="Button 1 href" value={cta.btn1_href} onChange={v => set("cta","btn1_href",v)} />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <Field label="Button 2 text" value={cta.btn2_text} onChange={v => set("cta","btn2_text",v)} />
          <Field label="Button 2 href" value={cta.btn2_href} onChange={v => set("cta","btn2_href",v)} />
        </div>
      </SectionBlock>
    </div>
  );
}
